"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { useActiveStore } from "@/store/activeStore";
import {
  LifecycleStage, STAGE_LABEL,
  TrendListPaginated,
} from "@/types";
import CategoryFilterChips from "@/components/ui/CategoryFilterChips";
import { KeywordCard } from "@/components/keyword/KeywordCard";
import { LifecycleBadge } from "@/components/ui/LifecycleBadge";
import { STAGE_META } from "@/lib/lifecycleStages";   // 2026-05-18 stage wording single source
import { SeasonalTagLegend } from "@/components/dashboard/SeasonalTagLegend";

type FilterKey = LifecycleStage | "all";
type SortKey = "score" | "surge";

const STAGE_OPTIONS: { key: FilterKey; label: string; emoji: string }[] = [
  { key: "all",       label: "전체",   emoji: "📋" },
  { key: "emerging",  label: "태동기", emoji: "🌱" },
  { key: "growing",   label: "성장기", emoji: "🔥" },
  { key: "mature",    label: "성숙기", emoji: "📊" },
  { key: "declining", label: "하락기", emoji: "📉" },
];

// 룰 v4 (2026-05-07): "점수순" → "기본순". 점수 자체 표시 X 됐지만 정렬 기준은 백엔드 유지
// (stage 별 자동 분기 — emerging=m7 / 그 외=normalized_score)
const SORT_OPTIONS: { key: SortKey; label: string; emoji: string }[] = [
  { key: "score", label: "기본순",   emoji: "⭐" },
  { key: "surge", label: "급상승순", emoji: "📈" },
];

const PAGE_SIZE = 20;

export default function TrendsListPage() {
  return (
    <Suspense fallback={<p className="text-text-sub text-center py-8">불러오는 중...</p>}>
      <TrendsListInner />
    </Suspense>
  );
}

function TrendsListInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { token, isLoggedIn } = useAuthStore();

  // 사용자 결정 2026-05-14 — ?recommended=1 일 때 갭 분석 추천 키워드만 (홈 카드 더보기 연결)
  const recommendedMode = searchParams.get("recommended") === "1";

  const stageParam = searchParams.get("stage") as FilterKey | null;
  const activeStage: FilterKey =
    stageParam && STAGE_OPTIONS.some((o) => o.key === stageParam) ? stageParam : "all";
  const activeCategory = searchParams.get("category") ?? "";
  const sortParam = searchParams.get("sort") as SortKey | null;
  const activeSort: SortKey =
    sortParam && SORT_OPTIONS.some((o) => o.key === sortParam) ? sortParam : "score";

  // 2026-05-15 fix — 옛 코드는 hooks 이전 early return = React error #300 (CLAUDE.md 증상 6 #7).
  // 모든 hooks 호출 후 마지막에 조건부 렌더로 변경.

  const [data, setData] = useState<TrendListPaginated | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");

  // 검색어 debounce (사용자 결정 2026-05-05 — server-side q 검색)
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // 첫 로드 / stage / category / sort / search 변경 시
  useEffect(() => {
    if (recommendedMode) return;   // 추천 mode 는 fetch X (RecommendedTrendsView 가 자체 fetch)
    setLoading(true);
    setPage(1);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params: Record<string, string | number> = { size: PAGE_SIZE, page: 1, sort: activeSort };
    if (activeStage !== "all") params.stage = activeStage;
    if (activeCategory) params.category = activeCategory;
    if (debouncedSearch) params.q = debouncedSearch;
    axios
      .get<TrendListPaginated>("/api/trends/list", { headers, params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [recommendedMode, activeStage, activeCategory, activeSort, debouncedSearch, token]);

  // 추천 mode = 별도 view (모든 hooks 호출 후 조건부 렌더)
  if (recommendedMode) {
    return <RecommendedTrendsView />;
  }

  const loadMore = async () => {
    if (!data || loadingMore) return;
    setLoadingMore(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const nextPage = page + 1;
      const params: Record<string, string | number> = { size: PAGE_SIZE, page: nextPage, sort: activeSort };
      if (activeStage !== "all") params.stage = activeStage;
      if (activeCategory) params.category = activeCategory;
      if (debouncedSearch) params.q = debouncedSearch;
      const r = await axios.get<TrendListPaginated>("/api/trends/list", { headers, params });
      // 2026-05-18 — 페이지 경계 중복 dedupe (id 기준 safety net, 백엔드 stable sort 와 별개로)
      const existingIds = new Set(data.keywords.map((k) => k.id));
      const merged = [...data.keywords, ...r.data.keywords.filter((k) => !existingIds.has(k.id))];
      setData({ ...r.data, keywords: merged });
      setPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  };

  const changeStage = (stage: FilterKey) => {
    const p = new URLSearchParams(searchParams.toString());
    if (stage === "all") p.delete("stage");
    else p.set("stage", stage);
    const qs = p.toString();
    router.push(qs ? `/trends?${qs}` : "/trends");
  };

  const changeSort = (sort: SortKey) => {
    const p = new URLSearchParams(searchParams.toString());
    if (sort === "score") p.delete("sort");
    else p.set("sort", sort);
    const qs = p.toString();
    router.push(qs ? `/trends?${qs}` : "/trends");
  };

  return (
    <div className="pb-24">
      {/* 헤더 sticky */}
      <div className="sticky top-0 z-40 bg-bg/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => router.back()} className="text-text-sub hover:text-text text-xl">←</button>
          <h1 className="text-lg font-black text-text">전체 트렌드</h1>
        </div>
      </div>

      {/* fallback 배너 — 위로 이동 (사용자 결정 2026-05-06) */}
      {data?.is_fallback && (
        <div className="mx-4 mt-3 py-2 px-3 rounded-lg bg-primary/10 text-primary text-xs text-center">
          📅 오늘자 수집 전 — {new Date(data.date).getMonth() + 1}월 {new Date(data.date).getDate()}일 기준
        </div>
      )}

      {/* 검색 input — 가장 자주 사용되어 위로 이동 */}
      <div className="px-4 pt-3">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="키워드 검색 (예: 버터떡)"
            className="w-full bg-bg-secondary text-text text-sm pl-10 pr-3 py-2.5 rounded-full border border-border outline-none focus:border-primary/50"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-sub text-sm">🔍</span>
        </div>
      </div>

      {/* 필터 영역 — 라벨 제거 + 압축 (사용자 결정 2026-05-06) */}
      <div className="px-4 pt-3 space-y-2">
        {/* 라이프사이클 chips */}
        <div className="flex gap-1.5 overflow-x-auto hide-scrollbar">
          {STAGE_OPTIONS.map((opt) => {
            const active = opt.key === activeStage;
            return (
              <button
                key={opt.key}
                onClick={() => changeStage(opt.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  active
                    ? "bg-primary text-bg"
                    : "border border-border text-text-sub hover:text-text hover:bg-bg-elevated"
                }`}
              >
                {opt.emoji} {opt.label}
              </button>
            );
          })}
        </div>

        {/* 카테고리 + 정렬 한 줄 */}
        <div className="flex gap-2 items-start flex-wrap">
          <div className="flex-1 min-w-0">
            <CategoryFilterChips useUrlQuery />
          </div>
          <div className="flex gap-1.5 shrink-0">
            {SORT_OPTIONS.map((opt) => {
              const active = opt.key === activeSort;
              return (
                <button
                  key={opt.key}
                  onClick={() => changeSort(opt.key)}
                  className={`px-2.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                    active
                      ? "bg-primary text-bg"
                      : "border border-border text-text-sub hover:text-text hover:bg-bg-elevated"
                  }`}
                  title={opt.key === "surge" ? "급상승 = 최근 7일 평균 > 30일 평균 (하락기·검색 식음 자동 제외)" : "최근 30일 활발도 높은 순"}
                >
                  {opt.emoji}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* StageDescription 컴팩트 (선택된 stage 만, 1줄 toast) */}
      {activeStage !== "all" && <CompactStageDesc stage={activeStage as LifecycleStage} />}

      {/* 시즌 태그 (🌸 매해 / ✨ 단발) 간략 설명 — 2026-05-20 사용자 결정 */}
      <div className="mx-4 mt-1">
        <SeasonalTagLegend />
      </div>

      {/* 카운트 + 점수 라벨 inline */}
      <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-2 text-2xs">
        {loading ? (
          <p className="text-text-sub">불러오는 중...</p>
        ) : data ? (
          <>
            <p className="text-text-sub">
              {debouncedSearch ? (
                <>🔍 <span className="text-primary font-semibold">"{debouncedSearch}"</span> </>
              ) : (
                <>{activeStage === "all" ? "전체" : STAGE_LABEL[activeStage as LifecycleStage]} </>
              )}
              <span className="text-text font-semibold">{data.total.toLocaleString()}</span>건
              {activeSort === "surge" && <span className="ml-1 opacity-70">· 급상승순</span>}
            </p>
            <p className="text-text-sub opacity-70 whitespace-nowrap">최근 30일 SNS 언급</p>
          </>
        ) : null}
      </div>

      {/* 리스트 */}
      <div className="px-4">
        {loading ? null : !data || data.keywords.length === 0 ? (
          <p className="text-text-sub text-center py-8">키워드가 없습니다.</p>
        ) : (
          <>
            <div className="space-y-1.5">
              {data.keywords.map((kw, idx) => (
                <KeywordCard
                  key={kw.id}
                  kw={kw}
                  rank={idx + 1}
                  showOverallRank={!!debouncedSearch}
                  contextStage={activeStage === "all" ? "all" : (activeStage as LifecycleStage)}
                />
              ))}
            </div>

            {/* 더 보기 */}
            {data.has_more && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full mt-3 py-3 text-sm font-semibold text-text-sub border border-border rounded-2xl hover:bg-bg-elevated transition-colors disabled:opacity-50"
              >
                {loadingMore ? "불러오는 중..." : "더 보기"}
              </button>
            )}

            {/* 티어 제한 마스킹 */}
            {data.locked && !isLoggedIn && (
              <div className="card mt-3 text-center py-5">
                <p className="text-sm font-semibold text-text">🔒 로그인하면 더 볼 수 있어요</p>
                <p className="text-xs text-text-sub mt-1">
                  {data.total - data.keywords.length}건이 더 있습니다
                </p>
                <Link href="/login" className="btn-primary text-sm mt-3 inline-block">
                  로그인
                </Link>
              </div>
            )}
            {data.locked && isLoggedIn && (
              <div className="card mt-3 text-center py-5 border-primary/30">
                <p className="text-sm font-semibold text-text">🔥 유료 플랜으로 전체 열람</p>
                <p className="text-xs text-text-sub mt-1">
                  {data.total - data.keywords.length}건이 더 있습니다
                </p>
                <Link href="/mypage/subscribe" className="btn-primary text-sm mt-3 inline-block">
                  구독하기
                </Link>
              </div>
            )}

            {!data.has_more && !data.locked && data.keywords.length > 0 && (
              <p className="text-2xs text-text-sub text-center mt-3">이게 전부예요 🎉</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ───────────── 단계별 컴팩트 가이드 (1줄 toast) ───────────── */

function CompactStageDesc({ stage }: { stage: LifecycleStage }) {
  const d = STAGE_META[stage];
  if (!d) return null;
  // 2026-05-19 — 위 stage chip 과 중복이라 "🔥 성장기" 라벨 제거, 설명만 노출
  return (
    <div className={`mx-4 mt-2 px-3 py-2 rounded-lg border text-2xs ${d.cls}`}>
      <span className="text-text-sub">{d.tip}</span>
    </div>
  );
}

/* ───── 추천 모드 (사용자 결정 2026-05-14, 홈 GapAnalysisCard 더보기) ───── */

interface GapMenu {
  keyword: string;
  keyword_id: number | null;
  category: string;
  stage: string | null;
  score_percentile: number;
  m30: number;
  supplier_count: number;
}
interface GapAnalysisOut {
  store_id: number;
  store_name: string;
  recommended: GapMenu[];
  secondary: GapMenu[];
  has_menus: boolean;
}

function RecommendedTrendsView() {
  const router = useRouter();
  const { token, isLoggedIn } = useAuthStore();
  const { activeStoreId } = useActiveStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const [data, setData] = useState<GapAnalysisOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"no_login" | "no_store" | "fetch_error" | null>(null);

  useEffect(() => {
    if (!hasHydrated) return;
    if (!isLoggedIn || !token) {
      setError("no_login");
      return;
    }
    if (!activeStoreId) {
      setError("no_store");
      return;
    }
    setLoading(true);
    axios
      .get<GapAnalysisOut>(`/api/stores/${activeStoreId}/gap-analysis`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { setData(r.data); setError(null); })
      .catch(() => setError("fetch_error"))
      .finally(() => setLoading(false));
  }, [hasHydrated, isLoggedIn, token, activeStoreId]);

  const all = [...(data?.recommended ?? []), ...(data?.secondary ?? [])];

  return (
    <div className="pb-24">
      <div className="sticky top-0 z-40 bg-bg/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => router.back()} className="text-text-sub hover:text-text text-xl">←</button>
          <h1 className="text-lg font-black text-text">💡 내 메뉴에 어울릴 트렌드 추천</h1>
        </div>
      </div>

      <div className="px-4 pt-4">
        <p className="text-2xs text-text-sub leading-relaxed mb-3">
          내 매장에 부족한 카테고리를 강세 트렌드 키워드로 추천. 30일 언급량·공급처 수 기준 정렬.
        </p>

        {error === "no_login" && (
          <div className="card text-center py-6">
            <p className="text-sm text-text">🔒 로그인 후 이용 가능</p>
            <Link href="/login" className="btn-primary text-sm mt-3 inline-block">로그인 →</Link>
          </div>
        )}
        {error === "no_store" && (
          <div className="card text-center py-6">
            <p className="text-sm text-text">🏪 매장 등록 후 이용 가능</p>
            <Link href="/store/setup" className="btn-primary text-sm mt-3 inline-block">매장 등록 →</Link>
          </div>
        )}
        {error === "fetch_error" && (
          <p className="text-text-sub text-center py-8">데이터를 불러올 수 없어요.</p>
        )}
        {loading && <p className="text-text-sub text-center py-8">불러오는 중...</p>}

        {!loading && !error && data && all.length === 0 && (
          <p className="text-text-sub text-center py-8">
            추천할 트렌드가 없어요. 새 트렌드가 떠오르면 알려드릴게요.
          </p>
        )}

        {!loading && !error && all.length > 0 && (
          <div className="space-y-2">
            {all.map((rec, i) => {
              const inner = (
                <div className="card flex items-center gap-2.5 py-2.5 px-3 hover:border-primary/40 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center text-sm shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="text-sm font-bold text-text truncate">{rec.keyword}</span>
                      {rec.stage && (
                        <LifecycleBadge stage={rec.stage as LifecycleStage} size="sm" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-2xs text-text-sub flex-wrap">
                      <span className="font-semibold text-text">{rec.category}</span>
                      <span>·</span>
                      <span>30일 언급 {rec.m30.toLocaleString()}건</span>
                      {rec.supplier_count > 0 && (
                        <>
                          <span>·</span>
                          <span className="text-primary font-semibold">제품 {rec.supplier_count}개</span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className="text-2xs font-bold text-primary shrink-0">상세 →</span>
                </div>
              );
              return rec.keyword_id ? (
                <Link key={`${rec.keyword}-${i}`} href={`/trend/${rec.keyword_id}`}>{inner}</Link>
              ) : (
                <div key={`${rec.keyword}-${i}`}>{inner}</div>
              );
            })}
          </div>
        )}

        <div className="mt-6 text-center">
          <Link href="/trends" className="text-2xs text-text-sub hover:text-text underline">
            ← 전체 트렌드 보기
          </Link>
        </div>
      </div>
    </div>
  );
}

